import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import './App.css';
import {
  checkAdminAuth,
  loginAdmin,
  fetchAllUsers,
  fetchPolicies,
  verifyAdminOtp,
  logoutAdmin,
  setAdminAuthToken,
} from './api/adminClient';
import {
  initInactivityService,
  markAuthCompleted,
  markAuthEnded,
} from './services/inactivityService';
import ErrorBoundary from './components/common/ErrorBoundary';
import AdminLogin from './components/auth/AdminLogin';
import AdminOtpVerify from './components/auth/AdminOtpVerify';
import AppShell from './components/common/AppShell';
import {
  placeholderStats,
} from './utils/fallbackData';

const extractAuthToken = (response) => {
  if (!response) {
    return null;
  }

  const headerToken =
    response.headers?.authorization ||
    response.headers?.Authorization ||
    response.headers?.['x-admin-token'];

  if (headerToken) {
    const parts = headerToken.split(' ');
    return parts.length === 2 ? parts[1] : headerToken;
  }

  const data = response.data || {};

  return (
    data.token ||
    data.authToken ||
    data.adminToken ||
    data.accessToken ||
    data?.data?.token ||
    data?.data?.adminToken ||
    null
  );
};

const normalizeUsersPagination = (pagination, usersCount = 0) => {
  if (!pagination) {
    return null;
  }

  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const total = Math.max(
    0,
    toNumber(
      pagination.total ?? pagination.totalItems ?? pagination.count ?? usersCount,
      usersCount
    )
  );
  const limit = Math.max(1, toNumber(pagination.limit ?? pagination.perPage ?? 10, 10));
  const page = Math.max(1, toNumber(pagination.page ?? pagination.currentPage ?? 1, 1));
  const computedPages = Math.max(1, Math.ceil(total / limit));
  const pages = Math.max(
    1,
    toNumber(
      pagination.pages ?? pagination.totalPages ?? computedPages,
      computedPages
    )
  );

  return { total, limit, page, pages };
};

const AdminRoutes = (props) => {
  const location = useLocation();

  if (props.loading) {
    return (
      <div className="app-shell py-5">
        <div className="container">
          <div className="card-soft text-center">
            <div className="mb-3">
              <span className="badge bg-primary-subtle text-primary rounded-pill shadow-sm">
                Loading workspace
              </span>
            </div>
            <h1 className="display-6 fw-semibold mb-3">ToivoTek Master Control</h1>
            <p className="text-muted mb-0">Preparing secure admin console…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!props.isAuthenticated) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  return <AppShell {...props} />;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [authStage, setAuthStage] = useState('checking');
  const [pendingEmail, setPendingEmail] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(placeholderStats);
  const [dashboardActivity, setDashboardActivity] = useState([]);
  const [usersPagination, setUsersPagination] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersStatusFilter, setUsersStatusFilter] = useState('all');
  const [usersEmailSearch, setUsersEmailSearch] = useState('');
  const [usersDateSearch, setUsersDateSearch] = useState('');
  const [usersSort, setUsersSort] = useState('createdAt_desc');
  const [usersLoading, setUsersLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const [sortBy, sortOrder] = usersSort.split('_');
      const usersResponse = await fetchAllUsers({
        limit: 10,
        page: usersPage,
        ...(usersStatusFilter !== 'all' ? { status: usersStatusFilter } : {}),
        ...(usersEmailSearch ? { search: usersEmailSearch } : {}),
        ...(usersDateSearch ? { createdAtDate: usersDateSearch } : {}),
        sortBy,
        sortOrder,
      });
      if (usersResponse.data?.success) {
        const userList = usersResponse.data?.users || [];
        setUsers(userList);
        setUsersPagination(normalizeUsersPagination(usersResponse.data?.pagination, userList.length));
      } else {
        setUsers([]);
        setUsersPagination(null);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]);
      setUsersPagination(null);
    } finally {
      setUsersLoading(false);
    }
  }, [usersPage, usersStatusFilter, usersEmailSearch, usersDateSearch, usersSort]);

  useEffect(() => {
    if (isAuthenticated && ['/users', '/dashboard', '/analytics'].includes(window.location?.pathname)) {
      loadUsers();
    }
  }, [loadUsers, isAuthenticated]);

  const loadAdminData = useCallback(async () => {
    const pathname = window.location?.pathname || '';
    if (pathname.startsWith('/admin/login')) {
      setIsAuthenticated(false);
      setAuthStage('login');
      markAuthEnded();
      setIsBootstrapping(false);
      setLoading(false);
      return;
    }

    if (pathname.startsWith('/admin/verify')) {
      setIsAuthenticated(false);
      setAuthStage('otp');
      markAuthEnded();
      setIsBootstrapping(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const authenticated = await checkAdminAuth();
      console.log('Authentication check result:', authenticated);
      setIsAuthenticated(authenticated);
      setAuthStage(authenticated ? 'authenticated' : 'login');
      if (authenticated) {
        markAuthCompleted();
      } else {
        markAuthEnded();
      }
      if (!authenticated) {
        console.warn('Authentication check failed, staying on current page');
        return;
      }

      if (pathname === '/policies') {
        console.log('Loading policies data');
        const policiesResponse = await fetchPolicies();
        const policyList = policiesResponse.data?.data || [];
        setPolicies(policyList);
      }
      console.log('Admin data loaded successfully');
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        setAuthStage('login');
        markAuthEnded();
      }
    } finally {
      setIsBootstrapping(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const handleLogin = useCallback(
    async ({ email, turnstileToken }) => {
      setLoading(true);
      try {
        const response = await loginAdmin(email, turnstileToken);
        console.log('Login response:', response);
        
        if (response.data?.success) {
          const token = extractAuthToken(response);
          if (token) {
            setAdminAuthToken(token);
          } else {
            console.warn('Login response did not include an auth token');
          }
          // OTP sent successfully, adminUnverifiedToken cookie is automatically set
          setPendingEmail(email);
          setAuthStage('otp');
          navigate('/admin/verify');
        } else {
          throw new Error(response.data?.error || 'Login failed');
        }
      } catch (error) {
        console.error('Login failed:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Login failed. Please try again.';
        alert(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  const handleVerifyOtp = useCallback(
    async (otp) => {
      setLoading(true);
      console.log('Verifying OTP:', { otp: otp.substring(0, 2) + '****' });
      try {
        const response = await verifyAdminOtp(otp);
        console.log('OTP verification successful:', response);

        if (response.data?.success) {
          // Authentication is handled via HTTP-only cookies set by the backend
          // No need to extract or store tokens in localStorage
          // Authentication successful, adminToken cookie is automatically set
          // adminUnverifiedToken cookie is automatically cleared
          setAuthStage('authenticated');
          setIsAuthenticated(true);
          setPendingEmail('');

          // Wait for authentication check and data loading to complete before navigating
          try {
            await loadAdminData();
            // Only navigate if authentication check passes
            window.location.href = '/dashboard';
          } catch (authError) {
            console.error('Authentication check failed after OTP verification:', authError);
            // If authentication check fails, redirect back to login
            setIsAuthenticated(false);
            setAuthStage('login');
            navigate('/admin/login', { replace: true });
          }
        } else {
          throw new Error(response.data?.error || 'OTP verification failed');
        }
      } catch (error) {
        console.error('OTP verification failed:', error);
        console.error('Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        const errorMessage = error.response?.data?.error || error.message || 'Invalid OTP. Please try again.';
        alert(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [loadAdminData, location.state?.from?.pathname, navigate]
  );

  const handleResendOtp = useCallback(async () => {
    if (!pendingEmail) {
      return;
    }
    await handleLogin({ email: pendingEmail });
  }, [handleLogin, pendingEmail]);

  const handleLogout = useCallback(async () => {
    markAuthEnded();
    try {
      await logoutAdmin();
      // All cookies are automatically cleared by the server
      setIsAuthenticated(false);
      setAuthStage('login');
      setPendingEmail('');
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if logout fails
      setIsAuthenticated(false);
      setAuthStage('login');
      setPendingEmail('');
      navigate('/admin/login', { replace: true });
    }
  }, [navigate]);

  // Idle admins get signed out after a period of inactivity
  useEffect(() => {
    return initInactivityService(handleLogout);
  }, [handleLogout]);

  const handleUsersStatusChange = useCallback((nextStatus) => {
    const normalized =
      !nextStatus || nextStatus === 'unverified' ? 'all' : nextStatus;
    setUsersStatusFilter(normalized);
    setUsersPage(1);
  }, []);

  const handleUsersEmailSearch = useCallback((email) => {
    setUsersEmailSearch(email);
    setUsersPage(1);
  }, []);

  const handleUsersDateSearch = useCallback((date) => {
    setUsersDateSearch(date);
    setUsersPage(1);
  }, []);

  const handleUsersSortChange = useCallback((sort) => {
    setUsersSort(sort);
    setUsersPage(1);
  }, []);

  if (isBootstrapping) {
    return (
      <div className="app-shell py-5">
        <div className="container">
          <div className="card-soft text-center">
            <div className="mb-3">
              <span className="badge bg-primary-subtle text-primary rounded-pill shadow-sm">
                Initializing
              </span>
            </div>
            <h1 className="display-6 fw-semibold mb-3">ToivoTek Master Control</h1>
            <p className="text-muted mb-0">Verifying your session…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/admin/login"
        element={<AdminLogin loading={loading} onSubmit={handleLogin} />}
      />
      <Route
        path="/admin/verify"
        element={
          authStage === 'otp' ? (
            <AdminOtpVerify
              loading={loading}
              email={pendingEmail}
              onResend={handleResendOtp}
              onVerify={handleVerifyOtp}
            />
          ) : (
            <Navigate to="/admin/login" replace />
          )
        }
      />
      <Route element={<Outlet />}> </Route>
      <Route
        path="/*"
        element={
          <AdminRoutes
            loading={loading}
            isAuthenticated={isAuthenticated}
            dashboardStats={dashboardStats}
            dashboardActivity={dashboardActivity}
            users={users}
            usersLoading={usersLoading}
            usersPagination={usersPagination}
            usersStatusFilter={usersStatusFilter}
            usersSort={usersSort}
            onUsersPageChange={setUsersPage}
            onUsersStatusChange={handleUsersStatusChange}
            onUsersEmailSearch={handleUsersEmailSearch}
            onUsersDateSearch={handleUsersDateSearch}
            onUsersSortChange={handleUsersSortChange}
            onRefreshUsers={loadUsers}
            policies={policies}
            subscriptions={subscriptions}
            transactions={transactions}
            onLogout={handleLogout}
          />
        }
      />
    </Routes>
  );
}

export default function AppWithRouter() {
  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
      <App />
    </Router>
    </ErrorBoundary>
  );
}